import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { lazy, Suspense } from "react";
import logo from "@/assets/mind-media-logo.svg";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import { Footer } from "@/components/ui/footer";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import {
  allPostsQueryOptions,
  postBySlugQueryOptions,
  sidebarWidgetsQueryOptions,
} from "@/lib/blog";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { PostCard } from "@/components/blog/PostCard";
import { PostBody } from "@/components/blog/PostBody";
import { formatDate } from "@/components/blog/format";
import wendelAvatar from "@/assets/wendel-santos-avatar.webp";

const ContactSection = lazy(() =>
  import("@/components/sections/ContactSection").then((m) => ({ default: m.ContactSection })),
);

const SIDEBAR_ID = "blog-sidebar";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ loaderData }) => {
    const post = (
      loaderData as
        | {
            post?: {
              title: string;
              excerpt: string;
              cover: string;
              slug: string;
              date: string;
              author: { name: string };
            };
          }
        | undefined
    )?.post;
    if (!post) {
      return {
        meta: [{ title: "Article — Mind Media Blog" }, { name: "robots", content: "noindex" }],
      };
    }
    const url = `https://mindmedia.co/blog/${post.slug}`;
    const description = post.excerpt || `Read ${post.title} on the Mind Media blog.`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description,
      ...(post.cover ? { image: post.cover } : {}),
      author: { "@type": "Person", name: "Wendel Santos" },
      publisher: {
        "@type": "Organization",
        name: "Mind Media",
        logo: { "@type": "ImageObject", url: "https://mindmedia.co/mind-media-logo.svg" },
      },
      datePublished: post.date,
      dateModified: post.date,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url,
    };
    return {
      meta: [
        { title: `${post.title} | Mind Media Blog` },
        { name: "description", content: description },
        { property: "og:title", content: post.title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(post.cover ? [{ property: "og:image", content: post.cover }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: description },
        ...(post.cover ? [{ name: "twitter:image", content: post.cover }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  loader: async ({ context, params }) => {
    const [post] = await Promise.all([
      context.queryClient.ensureQueryData(postBySlugQueryOptions(params.slug)),
      context.queryClient.ensureQueryData(sidebarWidgetsQueryOptions(SIDEBAR_ID)),
      context.queryClient.ensureQueryData(allPostsQueryOptions),
    ]);
    if (!post) throw notFound();
    return { post };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-gray-950">Article not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        The article you're looking for doesn't exist or has been moved.
      </p>
      <Link
        to="/blog"
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue"
      >
        ← Back to blog
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-destructive">{error.message}</p>
    </main>
  ),
});

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { data: post } = useSuspenseQuery(postBySlugQueryOptions(slug));
  const { data: widgets } = useSuspenseQuery(sidebarWidgetsQueryOptions(SIDEBAR_ID));
  const { data: allData } = useSuspenseQuery(allPostsQueryOptions);

  if (!post) return null;

  // Related posts: same category, exclude current, take 3.
  const related = allData.posts
    .filter(
      (p) => p.slug !== post.slug && p.categorySlugs.some((s) => post.categorySlugs.includes(s)),
    )
    .slice(0, 3);

  return (
    <main className="min-h-screen bg-background">
      <div className="relative">
        <section className="relative z-20 pb-2 text-center">
          <HeroNav
            logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
            navigation={[
              { label: "Work", href: "/#work" },
              { label: "About", href: "/#about" },
              { label: "Services", href: "/services" },
              { label: "Pricing", href: "/#pricing" },
              { label: "FAQ", href: "/#faq" },
              { label: "Contact", href: "/#contact" },
            ]}
            ctaButton={{ label: "Book a Meeting", href: "/#contact" }}
          />
        </section>
      </div>

      <section className="mx-auto max-w-7xl px-4 pt-8 pb-16 sm:px-6 sm:pt-12 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-3">
          {/* Article */}
          <article className="lg:col-span-2">
            <span className="inline-block rounded-full bg-brand-blue/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-blue">
              {post.categoryLabel}
            </span>
            <h1 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight text-gray-950 sm:text-3xl lg:text-4xl">
              {post.title}
            </h1>
            {null}

            <div className="mt-6 flex items-center gap-2.5">
              {post.author.avatar ? (
                <img
                  src={post.author.avatar}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : null}
              <span className="text-sm font-medium text-gray-800">{post.author.name}</span>
              <span className="text-sm text-gray-400">·</span>
              <span className="text-sm text-gray-500">{formatDate(post.date)}</span>
            </div>

            {post.cover ? (
              <img
                src={post.cover}
                alt={post.title}
                className="mt-8 w-full rounded-2xl object-cover shadow-sm"
              />
            ) : null}

            <div className="mt-10">
              {post.contentHtml ? (
                <PostBody html={post.contentHtml} />
              ) : (
                <p className="text-gray-600">This article has no content yet.</p>
              )}
            </div>

            {/* Author bio card */}
            <div className="mt-12 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5 sm:p-6">
              <div className="flex items-start gap-4">
                <img
                  src={post.author.avatar || wendelAvatar}
                  alt={post.author.name}
                  className="h-14 w-14 flex-shrink-0 rounded-full object-cover"
                />
                <div>
                  <p className="font-display text-base font-bold text-gray-950">
                    Your website isn't just a logo and some pretty colors. It's a growth engine.
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    Fix these mistakes, and you'll turn more visitors into leads, leads into
                    clients, and clients into raving fans.
                  </p>
                  <Link
                    to="/"
                    hash="about"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:opacity-80"
                  >
                    More about me <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <BlogSidebar
              categories={allData.categories}
              totalCount={allData.posts.length}
              widgets={widgets}
            />
          </div>
        </div>

        {/* Related */}
        {related.length > 0 ? (
          <div className="mt-16">
            <h2 className="mb-5 font-display text-xl font-bold text-gray-950 sm:text-2xl">
              More articles you might like
            </h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <Suspense fallback={<div className="min-h-[80px]" />}>
        <ContactSection compact />
      </Suspense>

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/the-problem", label: "The Problem" },
          { href: "/#work", label: "Work" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}
